<template>
  <LoadingWrapper :state="getFeedbackRequestStatus">
    <FullScreenLayoutContent>
      <template #header>
        <BaseTextButton
          color="text"
          icon="arrow_back"
          @click="router.push({ name: 'dashboard' })"
          class="absolute top-4 left-4"
          >Zur Startseite</BaseTextButton
        >
      </template>
      <div class="flex h-dvh w-full flex-col items-center justify-center gap-4">
        <div class="flex flex-col items-center gap-4">
          <div class="rounded-medium flex flex-col gap-2 self-start">
            <div>
              <TimeAgo class="text-label-large text-outline" :date="feedbackRequest.createdAt" />
              <p class="text-title-large">Feedback Anfrage von {{ receiverName }}</p>
            </div>
            <div class="flex gap-2">
              <BaseChip
                v-for="feedbackTopic of feedbackRequest.feedbackTopics"
                :key="feedbackTopic.id"
                :icon="feedbackTopic.icon"
                type="assist"
                >{{ feedbackTopic.title }}</BaseChip
              >
            </div>
          </div>
          <div class="flex gap-2">
            <BaseMemberAvatar :member="user" size="128" class="mb-2 self-end" />
            <div class="relative flex w-20 flex-col items-center justify-center">
              <BaseIcon size="40" class="text-primary z-20">arrow_right_alt</BaseIcon>
              <FeedbackFlow direction="right" class="absolute! w-full" />
            </div>
            <div class="relative self-end">
              <BaseShapeMask
                shape="9-sided-cookie"
                :scale="1.1"
                class="relative z-10"
                animation="spin"
              >
                <BaseMemberAvatar :member="feedbackRequest.receiver" size="160" />
              </BaseShapeMask>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <span v-if="feedbackRequest.title" class="text-title-large">{{
              feedbackRequest.title
            }}</span>
            <WysiwygTextFieldReadOnly
              v-if="feedbackRequest.message"
              :value="feedbackRequest.message"
            />
          </div>
        </div>
        <BaseButtonGroup class="mt-8">
          <BaseTextButton icon="close" size="M" shape="round" color="tonal" @click="openDialog"
            >Ablehnen</BaseTextButton
          >
          <BaseTextButton size="M" color="filled" icon="chat_bubble" @click="accept"
            >Feedback geben</BaseTextButton
          >
        </BaseButtonGroup>
      </div>
      <BaseDialog
        type="basic"
        :is-open="isOpen"
        @close="closeDialog"
        title="Anfrage ablehnen"
        description="Möchtest du die Feedbackanfrage wirklich ablehnen?"
      >
        <BaseInputField
          class="bg-surface-container-high mt-2"
          label="Begründung"
          type="multiText"
          v-model="rejectionReason"
        />
        <template #confirm
          ><BaseTextButton @click="clickReject">Anfrage ablehnen</BaseTextButton></template
        >
        <template #dismiss
          ><BaseTextButton @click="closeDialog">{{ $t('common.cancel') }}</BaseTextButton></template
        >
      </BaseDialog>
    </FullScreenLayoutContent>
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseMemberAvatar from '../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import TimeAgo from '../../../core/components/base/time-ago/TimeAgo.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import BaseShapeMask from '../../../core/components/base/shapes/BaseShapeMask.vue'
  import BaseButtonGroup from '../../../core/components/base/button/BaseButtonGroup.vue'
  import BaseDialog from '../../../core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import BaseInputField from '../../../core/components/base/form-fields/BaseInputField.vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useGetFeedbackRequest } from '../../../core/composables/use-get-feedback-request'
  import { useMutateFeedbackRequest } from '../../../core/composables/use-mutate-feedback-request'
  import LoadingWrapper from '../../../core/components/loading/LoadingWrapper.vue'
  import FullScreenLayoutContent from '../../../core/components/layouts/FullScreenLayoutContent.vue'
  import FeedbackFlow from '../../../core/components/feedback-flow/FeedbackFlow.vue'
  import { useUserStore } from '../../../stores/user.store'
  import WysiwygTextFieldReadOnly from '../../../core/components/base/wysiwyg/WysiwygTextFieldReadOnly.vue'
  import BaseChip from '../../../core/components/base/chip/BaseChip.vue'
  import { memberFullName } from '@/core/models/member/member-name'

  const router = useRouter()
  const route = useRoute()
  const { user } = useUserStore()

  const receiverId = route.params.receiverId as string
  const surveyId = route.params.surveyId as string

  const { feedbackRequest, getFeedbackRequestStatus } = useGetFeedbackRequest({
    surveyId: surveyId,
    feedbackReceiverId: receiverId,
  })

  const { accept, reject } = useMutateFeedbackRequest({
    feedbackReceiverId: receiverId,
    surveyId: surveyId,
  })

  const clickReject = () => {
    reject(rejectionReason.value)
    closeDialog()
  }

  const { isOpen, openDialog, closeDialog } = useDialog()

  const rejectionReason = ref<string | undefined>(undefined)

  const receiverName = computed(() =>
    feedbackRequest.value?.receiver ? memberFullName(feedbackRequest.value.receiver) : '',
  )
</script>
